import type { CaptionTokenId } from "../contracts/ids.js";
import type { HeadlineTemplate, SourcePack } from "../contracts/source-pack.js";
import { captionMatchesGrammar } from "../validation/validate-edit-graph.js";

/** Resolve slot option selections into an ordered caption token sequence. */
export function expandHeadlineTemplate(
  template: HeadlineTemplate,
  selections: Readonly<Record<string, string>>,
): CaptionTokenId[] {
  const tokens: CaptionTokenId[] = [];
  for (const segment of template.segments) {
    if (segment.kind === "fixed") {
      tokens.push(...segment.tokenIds);
      continue;
    }
    const optionId = selections[segment.id];
    if (optionId === undefined) {
      throw new Error(`missing selection for slot "${segment.id}"`);
    }
    const option = segment.options.find((o) => o.id === optionId);
    if (!option) {
      throw new Error(`unknown option "${optionId}" for slot "${segment.id}"`);
    }
    tokens.push(...option.tokenIds);
  }
  return tokens;
}

/** Default slot → option map authored on the template. */
export function defaultHeadlineSelections(
  template: HeadlineTemplate,
): Record<string, string> {
  const selections: Record<string, string> = {};
  for (const segment of template.segments) {
    if (segment.kind === "slot") {
      selections[segment.id] = segment.defaultOptionId;
    }
  }
  return selections;
}

/** Stable key for a token expansion sequence. */
export function headlineExpansionKey(tokenIds: readonly string[]): string {
  return tokenIds.join("\u0001");
}

export type HeadlineExpansionChoice = {
  templateId: string;
  selections: Record<string, string>;
  tokenIds: CaptionTokenId[];
};

/**
 * Exhaustive cartesian product of every slot option combination with selections.
 * Fixed segments are constant; order follows template.segments.
 */
export function allHeadlineExpansionChoices(
  template: HeadlineTemplate,
): HeadlineExpansionChoice[] {
  const slots = template.segments.filter(
    (s): s is Extract<HeadlineTemplate["segments"][number], { kind: "slot" }> =>
      s.kind === "slot",
  );
  if (slots.length === 0) {
    const tokenIds = expandHeadlineTemplate(template, {});
    return [{ templateId: template.id, selections: {}, tokenIds }];
  }

  const optionLists = slots.map((slot) =>
    slot.options.map((option) => ({ slotId: slot.id, optionId: option.id })),
  );

  let combos: Record<string, string>[] = [{}];
  for (const options of optionLists) {
    const next: Record<string, string>[] = [];
    for (const partial of combos) {
      for (const choice of options) {
        next.push({ ...partial, [choice.slotId]: choice.optionId });
      }
    }
    combos = next;
  }
  return combos.map((selections) => ({
    templateId: template.id,
    selections,
    tokenIds: expandHeadlineTemplate(template, selections),
  }));
}

/**
 * Exhaustive cartesian product of every slot option combination.
 * Fixed segments are constant; order follows template.segments.
 */
export function allHeadlineExpansions(template: HeadlineTemplate): CaptionTokenId[][] {
  return allHeadlineExpansionChoices(template).map((c) => c.tokenIds);
}

/** True when every reachable expansion satisfies pack caption grammar. */
export function headlineTemplateExpansionsAreGrammatical(
  template: HeadlineTemplate,
  pack: SourcePack,
): boolean {
  return allHeadlineExpansions(template).every((tokenIds) =>
    captionMatchesGrammar(tokenIds, pack),
  );
}

/**
 * Assert every expansion across templates maps to exactly one template+selection.
 * Returns the expansion key map when valid; throws Error with a message when not.
 */
export function assertUniqueHeadlineExpansions(
  templates: readonly HeadlineTemplate[],
): Map<string, HeadlineExpansionChoice> {
  const byKey = new Map<string, HeadlineExpansionChoice>();
  for (const template of templates) {
    for (const choice of allHeadlineExpansionChoices(template)) {
      const key = headlineExpansionKey(choice.tokenIds);
      const prior = byKey.get(key);
      if (prior) {
        throw new Error(
          `duplicate headline expansion "${choice.tokenIds.join(" ")}" shared by templates "${prior.templateId}" and "${template.id}"`,
        );
      }
      byKey.set(key, choice);
    }
  }
  return byKey;
}
